import { useEffect, useState } from "react";
import { toast } from "sonner";
import type { Lang } from "../../lib/i18n";
import { pick } from "../../lib/i18n";
import { getTeam, subscribeTeam, saveTeam, resetTeam, newTeamMember, type TeamMember } from "../../data/team";

/** IT Team editor tab (ct) — add / edit / reorder / delete members, save & reset. */
export function TeamTab({ lang }: { lang: Lang }) {
  const t = (ar: string, en: string) => pick(lang, ar, en);
  const [members, setMembers] = useState<TeamMember[]>(() => getTeam());
  const [dirty, setDirty] = useState(false);

  useEffect(() => subscribeTeam(() => { setMembers(getTeam()); setDirty(false); }), []);

  const update = (id: string, patch: Partial<TeamMember>) => {
    setMembers((list) => list.map((m) => (m.id === id ? { ...m, ...patch } : m)));
    setDirty(true);
  };
  const add = () => { setMembers((list) => [...list, newTeamMember()]); setDirty(true); };
  const remove = (id: string) => {
    if (confirm(t("حذف هذا العضو؟", "Delete this member?"))) { setMembers((list) => list.filter((m) => m.id !== id)); setDirty(true); }
  };
  const move = (id: string, dir: -1 | 1) => {
    const i = members.findIndex((m) => m.id === id);
    if (i < 0) return;
    const j = i + dir;
    if (j < 0 || j >= members.length) return;
    const next = [...members];
    [next[i], next[j]] = [next[j], next[i]];
    setMembers(next);
    setDirty(true);
  };
  async function save() {
    const res = await saveTeam(members);
    res.ok ? (setDirty(false), toast.success(t("تم حفظ الفريق", "Team saved"))) : toast.error(t("تعذّر الحفظ: ", "Save failed: ") + (res.error || ""));
  }
  async function reset() {
    if (confirm(t("استعادة قائمة الفريق الافتراضية؟", "Restore the default IT team?"))) {
      const res = await resetTeam();
      res.ok ? toast.success(t("تمت الاستعادة", "Reset")) : toast.error(t("تعذّر الاستعادة: ", "Reset failed: ") + (res.error || ""));
    }
  }

  function uploadPhoto(id: string, file: File | undefined) {
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error(t("يُرجى اختيار ملف صورة", "Please choose an image file")); return; }
    if (file.size > 2 * 1024 * 1024) { toast.error(t("حجم الصورة كبير (الحد 2 ميجابايت)", "Image too large (max 2 MB)")); return; }
    const reader = new FileReader();
    reader.onload = () => update(id, { photo: reader.result as string });
    reader.onerror = () => toast.error(t("تعذّر قراءة الصورة", "Could not read the image"));
    reader.readAsDataURL(file);
  }

  return (
    <div className="kd-admin-team-editor">
      <div className="kd-admin-toolbar">
        <button className="kd-btn-primary kd-btn-gold" onClick={add}>+ {t("إضافة عضو", "Add Member")}</button>
        <button className="kd-btn-primary" onClick={save} disabled={!dirty}>{dirty ? t("حفظ التعديلات", "Save Changes") : t("محفوظ", "Saved")}</button>
        <button className="kd-btn-secondary kd-btn-danger" onClick={reset}>⟲ {t("استعادة الافتراضي", "Reset to defaults")}</button>
      </div>
      <div className="kd-admin-team-list">
        {members.map((m, i) => (
          <div className="kd-admin-team-card" key={m.id}>
            <div className="kd-admin-team-card-header">
              <div className="kd-admin-team-photo">
                {m.photo ? <img src={m.photo} alt={m.nameEn} onError={(e) => ((e.target as HTMLImageElement).style.opacity = "0.3")} /> : <span>{(m.nameEn || m.nameAr || "?")[0]}</span>}
              </div>
              <div style={{ flex: 1 }}>
                <strong>{m.nameAr || m.nameEn || t("عضو جديد", "New member")}</strong>
                <small style={{ display: "block", color: "var(--kd-text-muted)" }}>{m.titleEn || m.titleAr}</small>
              </div>
              <div className="kd-admin-team-actions">
                <button className="kd-admin-action-btn" onClick={() => move(m.id, -1)} disabled={i === 0} title={t("للأعلى", "Move up")}>↑</button>
                <button className="kd-admin-action-btn" onClick={() => move(m.id, 1)} disabled={i === members.length - 1} title={t("للأسفل", "Move down")}>↓</button>
                <button className="kd-admin-action-btn kd-admin-action-danger" onClick={() => remove(m.id)} title={t("حذف", "Delete")}>🗑</button>
              </div>
            </div>
            <div className="kd-admin-grid-2">
              <label><span>{t("الاسم بالعربية", "Name (Arabic)")}</span><input value={m.nameAr} onChange={(e) => update(m.id, { nameAr: e.target.value })} /></label>
              <label><span>{t("الاسم بالإنجليزية", "Name (English)")}</span><input value={m.nameEn} onChange={(e) => update(m.id, { nameEn: e.target.value })} /></label>
            </div>
            <div className="kd-admin-grid-2">
              <label><span>{t("المسمى الوظيفي (عربي)", "Title (Arabic)")}</span><input value={m.titleAr} onChange={(e) => update(m.id, { titleAr: e.target.value })} /></label>
              <label><span>{t("المسمى الوظيفي (إنجليزي)", "Title (English)")}</span><input value={m.titleEn} onChange={(e) => update(m.id, { titleEn: e.target.value })} /></label>
            </div>
            <div className="kd-admin-grid-2">
              <label><span>{t("البريد الإلكتروني", "Email")}</span><input type="email" value={m.email} onChange={(e) => update(m.id, { email: e.target.value })} placeholder="name@kaden-sa.com" /></label>
              <label><span>{t("رقم التحويلة", "Extension")}</span><input value={m.ext} onChange={(e) => update(m.id, { ext: e.target.value })} placeholder="346" /></label>
            </div>
            <label>
              <span>{t("صورة العضو", "Member photo")}</span>
              <div className="kd-admin-photo-upload">
                <input type="text" value={m.photo} onChange={(e) => update(m.id, { photo: e.target.value })} placeholder={t("مسار/رابط الصورة أو ارفع من جهازك", "Photo path/URL, or upload from your device")} />
                <label className="kd-admin-upload-btn">
                  ⬆ {t("رفع صورة", "Upload")}
                  <input type="file" accept="image/*" style={{ display: "none" }} onChange={(e) => { uploadPhoto(m.id, e.target.files?.[0]); e.target.value = ""; }} />
                </label>
                {m.photo && <button type="button" className="kd-admin-action-btn kd-admin-action-danger" title={t("إزالة الصورة", "Remove photo")} onClick={() => update(m.id, { photo: "" })}>✕</button>}
              </div>
            </label>
            <label><span>{t("الوصف بالعربية", "Description (Arabic)")}</span><textarea rows={2} value={m.descAr} onChange={(e) => update(m.id, { descAr: e.target.value })} /></label>
            <label><span>{t("الوصف بالإنجليزية", "Description (English)")}</span><textarea rows={2} value={m.descEn} onChange={(e) => update(m.id, { descEn: e.target.value })} /></label>
          </div>
        ))}
      </div>
    </div>
  );
}
